import { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import {
    Upload, MapPin, Shield, BookOpen,
    ChevronRight, Loader2, CheckCircle2, RefreshCcw
} from "lucide-react";

export default function CreateTeam() {
    const PROJECT_ID = "kzklgxnbtquhjfywtnsz";
    const BUCKET_NAME = "program_logos";
    const STORAGE_BASE_URL = `https://${PROJECT_ID}.supabase.co/storage/v1/object/public/${BUCKET_NAME}/`;

    const [form, setForm] = useState({
        name: "",
        program_id: "",
        sport: "Basketball",
        location_address: "",
        latitude: null,
        longitude: null,
        program_img: null,
        team_identity: "",
        age: [], 
        gender: "", 
    });

    const [programs, setPrograms] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [user, setUser] = useState(null);

    const ageOptions = ["8", "9", "10", "11", "12", "13", "14", "15", "16", "17", "18"];

    // Helper to handle mixed URL/Filename image paths
    const getSafeUrl = (path) => {
        if (!path) return null;
        if (path.startsWith("http")) return path;
        return `${STORAGE_BASE_URL}${path}`;
    };

    useEffect(() => {
        const init = async () => {
            const { data: { user: activeUser } } = await supabase.auth.getUser();
            setUser(activeUser);
            if (activeUser) fetchPrograms(activeUser.id);
        };
        init();
    }, []);

    const fetchPrograms = async (userId) => {
        const { data, error } = await supabase
            .from("programs")
            .select("id, name, program_logo")
            .eq("administrator", userId)
            .order("name", { ascending: true });

        if (error) console.error("Fetch Error:", error.message);
        else setPrograms(data);
    };

    const handleProgramSelect = (e) => {
        const selectedId = e.target.value;
        const selectedProgram = programs.find(p => p.id === selectedId);

        setForm(prev => ({
            ...prev,
            program_id: selectedId,
            // Automatically inherit the program logo into the team form
            program_img: selectedProgram?.program_logo || prev.program_img
        }));
    };

    const handleLogoUpload = async (e) => {
        try {
            setUploading(true);
            const file = e.target.files[0];
            if (!file) return;

            const fileName = `${Date.now()}_${file.name.replace(/\s/g, "_")}`;
            const { error: uploadError } = await supabase.storage
                .from(BUCKET_NAME)
                .upload(fileName, file);

            if (uploadError) throw uploadError;
            
            // Set the new filename as the team logo
            setForm(prev => ({ ...prev, program_img: fileName }));
        } catch (err) {
            alert("Upload Error: Check if 'program_logos' bucket is Public.");
            console.error(err);
        } finally {
            setUploading(false);
        }
    };

    const handleGeocode = async () => {
        if (!form.location_address) return;
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(form.location_address)}`
            );
            const data = await response.json();
            if (data?.[0]) {
                setForm(prev => ({
                    ...prev,
                    latitude: parseFloat(data[0].lat),
                    longitude: parseFloat(data[0].lon),
                }));
            } else {
                alert("Could not find coordinates for this address.");
            }
        } catch (err) {
            console.error("Geocoding failed:", err);
        }
    };

    const toggleAge = (value) => {
        setForm(prev => ({
            ...prev,
            age: prev.age.includes(value)
                ? prev.age.filter(v => v !== value)
                : [...prev.age, value],
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user) return;
        setIsSubmitting(true);

        const payload = {
            name: form.name,
            program_id: form.program_id,
            sport: form.sport,
            location_address: form.location_address,
            latitude: form.latitude,
            longitude: form.longitude,
            program_img: form.program_img,
            team_identity: form.team_identity,
            age: form.age.join(", "),
            gender: form.gender,
            location: form.longitude && form.latitude
                ? `POINT(${form.longitude} ${form.latitude})`
                : null,
        };

        const { error } = await supabase.from("teams").insert([payload]);

        setIsSubmitting(false);
        if (error) alert("Error: " + error.message);
        else alert("Team established successfully!");
    };

    if (!user) return <div className="h-screen flex items-center justify-center font-bold">Initializing Nuvio Sport...</div>;

    return (
        <div className="min-h-screen bg-[#f8fafc] p-6 pb-20">
            <div className="max-w-4xl mx-auto">
                <header className="mb-10 text-center md:text-left">
                    <h1 className="text-4xl font-black text-slate-900 tracking-tight uppercase italic">Establish Squad</h1>
                    <p className="text-slate-500 font-medium">Configure team identity and eligibility parameters.</p>
                </header>

                <form onSubmit={handleSubmit} className="space-y-8">

                    {/* IDENTITY & LOGO */}
                    <section className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8">
                        <div className="flex flex-col md:flex-row gap-8 items-center">
                            <div className="relative group w-32 h-32 shrink-0">
                                <div className="w-full h-full rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden transition-all group-hover:border-blue-400">
                                    {form.program_img ? (
                                        <img
                                            src={getSafeUrl(form.program_img)}
                                            alt="Team Logo"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="text-center p-4">
                                            <Upload className="mx-auto text-slate-400 mb-1" size={20} />
                                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-tighter">No Logo</p>
                                        </div>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                    onChange={handleLogoUpload}
                                    disabled={uploading}
                                />
                                {uploading && <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-full"><Loader2 className="animate-spin text-blue-600" /></div>}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 w-full">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Team Name</label>
                                    <input required className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Parent Program</label>
                                    <select required className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500" value={form.program_id} onChange={handleProgramSelect}>
                                        <option value="">Select Program</option>
                                        {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* LOGISTICS & COORDINATES */}
                    <section className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="px-8 py-5 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                            <MapPin size={20} className="text-blue-600" />
                            <h2 className="font-bold text-slate-700 uppercase tracking-widest text-sm">Location & Geocoding</h2>
                        </div>
                        <div className="p-8 space-y-4">
                            <div className="flex flex-col md:flex-row gap-3">
                                <input
                                    className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl outline-none"
                                    placeholder="Street Address, City, State"
                                    value={form.location_address}
                                    onChange={e => setForm({ ...form, location_address: e.target.value })}
                                />
                                <button type="button" onClick={handleGeocode} className="px-6 py-3 bg-slate-900 text-white rounded-2xl font-bold hover:bg-black transition flex items-center justify-center gap-2">
                                    <RefreshCcw size={16} /> Get Lat/Long
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex flex-col items-center">
                                    <p className="text-[10px] text-slate-400 font-bold uppercase">Latitude</p>
                                    <p className="text-sm font-mono font-bold text-slate-700">{form.latitude || "---"}</p>
                                </div>
                                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex flex-col items-center">
                                    <p className="text-[10px] text-slate-400 font-bold uppercase">Longitude</p>
                                    <p className="text-sm font-mono font-bold text-slate-700">{form.longitude || "---"}</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ELIGIBILITY */}
                    <section className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="px-8 py-5 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                            <Shield size={20} className="text-blue-600" />
                            <h2 className="font-bold text-slate-700 uppercase tracking-widest text-sm">Eligibility</h2>
                        </div>
                        <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-10">
                            <div className="space-y-4">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Gender</label>
                                <div className="flex gap-2">
                                    {["Male", "Female", "Co-ed"].map((g) => (
                                        <button
                                            key={g}
                                            type="button"
                                            onClick={() => setForm({ ...form, gender: g })}
                                            className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all border ${form.gender === g ? 'bg-blue-600 border-blue-600 text-white shadow-lg' : 'bg-white border-slate-200 text-slate-500'
                                                }`}
                                        >
                                            {g}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-4">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Ages</label>
                                <div className="grid grid-cols-4 gap-2">
                                    {ageOptions.map(age => (
                                        <button
                                            key={age}
                                            type="button"
                                            onClick={() => toggleAge(age)}
                                            className={`py-2 rounded-xl font-bold text-xs transition-all border ${form.age.includes(age) ? 'bg-slate-900 border-slate-900 text-white shadow-md' : 'bg-white border-slate-200 text-slate-500'
                                                }`}
                                        >
                                            {age}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>

                    <button
                        type="submit"
                        disabled={isSubmitting || uploading}
                        className="w-full py-5 bg-blue-600 hover:bg-blue-700 text-white rounded-[2rem] font-black text-xl shadow-2xl transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                    >
                        {isSubmitting ? <Loader2 className="animate-spin" /> : "DEPLOY TEAM"}
                        {!isSubmitting && <ChevronRight size={24} />}
                    </button>
                </form>
            </div>
        </div>
    );
}